import React, { FC, useCallback, useContext } from "react";
import EmojiPicker from "../../../lib/ui/EmojiPicker";
import RichTextEditorFormattingToolbar from "./RichTextEditorFormattingToolbar";
import { TiptapEditorContext } from "./contexts/TiptapEditorContext";
import {
  MediaFragment,
  TranslationSetFragment,
} from "@src/fragments.generated";
import AddMediaDialog from "@src/features/app/dialogs/addMedia/AddMediaDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import AiTextSuggestionsDropdown from "@src/features/aiAssist/AiTextSuggestionsDropdown";
import { Separator } from "@src/lib/ui/Separator";
import PhotoOutlineIcon from "@src/lib/icons/18px/photo-outline";
import { Button } from "@src/lib/ui/Button";
import { cn } from "@src/lib/utils/cn";
import EmojiOutlineIcon from "@src/lib/icons/18px/emoji-outline";
import { useAvailableLanguages } from "@src/features/contentDetail/hooks/useAvailableLanguages";
import { Switch } from "@src/lib/ui/Switch";
import { Label } from "@src/lib/ui/Label";
import LanguageIcon from "@src/lib/icons/18px/language";

type Props = {
  onMediaChange?: (media?: MediaFragment) => void;
  messageText?: TranslationSetFragment;
  setMessageText?: (text: TranslationSetFragment) => void;
  translationModeEnabled?: boolean;
  setTranslationModeEnabled?: (v: boolean) => void;
  hideRichTextHeaderOptions?: boolean;
};

const EditorActionsOld: FC<Props> = ({
  onMediaChange,
  messageText,
  setMessageText,
  translationModeEnabled,
  setTranslationModeEnabled,
  hideRichTextHeaderOptions,
}) => {
  const tiptapEditorContext = useContext(TiptapEditorContext);
  const onEmojiSelect = useCallback(
    (emoji: string) => {
      if (tiptapEditorContext?.editor) {
        const insertPoint =
          tiptapEditorContext.editor.view?.state?.selection?.anchor || 1;
        tiptapEditorContext.editor
          .chain()
          .focus()
          .insertContentAt(insertPoint, emoji, {
            parseOptions: {
              preserveWhitespace: false,
            },
          })
          .run();
      }
    },
    [tiptapEditorContext?.editor],
  );
  const { nonEnglishLanguagesInUse } = useAvailableLanguages();
  return (
    <TooltipProvider>
      <div
        className={cn(
          "flex h-full items-center rounded-b-xl bg-background py-0",
          !hideRichTextHeaderOptions && "pl-2.5 pr-1.5",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-1">
          <RichTextEditorFormattingToolbar
            hideRichTextHeaderOptions={hideRichTextHeaderOptions}
          />
          {onMediaChange && (
            <Tooltip>
              <TooltipTrigger>
                <AddMediaDialog
                  onMediaChange={onMediaChange}
                  onResourceChange={(trainingResource) => {
                    const newMedia = trainingResource.publishedVersion?.media;
                    if (!newMedia) return;
                    onMediaChange(newMedia);
                  }}
                  title="Add media"
                  trigger={
                    <Button size="icon" variant="ghost">
                      <PhotoOutlineIcon />
                    </Button>
                  }
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">Add media</TooltipContent>
            </Tooltip>
          )}
          <Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button size="icon" variant="ghost">
                    <EmojiOutlineIcon />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">Emoji</TooltipContent>
            </Tooltip>
            <PopoverContent
              side="right"
              align="start"
              className="z-[9999] w-auto p-0"
              sideOffset={5}
            >
              <EmojiPicker onEmojiSelect={onEmojiSelect} />
            </PopoverContent>
          </Popover>
          {messageText && setMessageText && (
            <>
              <div className="mx-2 flex self-stretch py-1">
                <Separator orientation="vertical" />
              </div>
              <div className="flex flex-1 justify-between self-stretch">
                <AiTextSuggestionsDropdown
                  messageText={messageText}
                  setMessageText={setMessageText}
                />
                {nonEnglishLanguagesInUse.length > 0 && (
                  <div className="mr-2 flex items-center gap-2">
                    <LanguageIcon />
                    <Label>Translation mode</Label>
                    <Switch
                      size="sm"
                      checked={translationModeEnabled}
                      onCheckedChange={() =>
                        setTranslationModeEnabled?.(!translationModeEnabled)
                      }
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default EditorActionsOld;
